import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ServiceCard } from "@/components/schedora/ServiceCard";
import { StaffCard } from "@/components/schedora/StaffCard";
import { cn } from "@/lib/utils";
import { SchedoraLogo } from "@/components/schedora/SchedoraLogo";
import { useApp } from "@/context/AppContext";
import { getAvailableTimeSlots, minutesToTimeString, timeStringToMinutes } from "@/lib/booking";

const stepLabels = ["Service", "Staff", "Date & Time", "Details", "Confirm"];

export default function CustomerBooking() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const appointmentId = searchParams.get("appointmentId") ?? undefined;
  const presetServiceId = searchParams.get("serviceId") ?? undefined;
  const { state, currentUser, currentBusiness, bookAppointment } = useApp();
  const editingAppointment = appointmentId
    ? state.appointments.find((entry) => entry.id === appointmentId)
    : undefined;
  const businessId = currentBusiness?.id ?? "business-1";
  const services = state.services.filter((entry) => entry.businessId === businessId && entry.active);
  const staff = state.staff.filter((entry) => entry.businessId === businessId);
  const client = state.clients.find((entry) => entry.id === currentUser?.clientId);
  const [step, setStep] = useState(0);
  const [selectedService, setSelectedService] = useState<string | null>(editingAppointment?.serviceId ?? presetServiceId ?? null);
  const [selectedStaff, setSelectedStaff] = useState<string | null>(editingAppointment?.staffId ?? null);
  const [selectedDate, setSelectedDate] = useState(editingAppointment?.date ?? "2026-04-10");
  const [selectedTime, setSelectedTime] = useState<string | null>(
    editingAppointment ? minutesToTimeString(editingAppointment.startMinutes) : null,
  );
  const [name, setName] = useState(client?.name ?? currentUser?.displayName ?? "");
  const [email, setEmail] = useState(client?.email ?? currentUser?.email ?? "");
  const [phone, setPhone] = useState(client?.phone ?? "");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const chosenService = services.find((s) => s.id === selectedService);
  const chosenStaff = staff.find((s) => s.id === selectedStaff);
  const availableTimeSlots = useMemo(() => {
    if (!selectedStaff || !chosenService) {
      return [];
    }

    return getAvailableTimeSlots({
      appointments: state.appointments.filter((entry) => entry.businessId === businessId),
      staffId: selectedStaff,
      date: selectedDate,
      duration: chosenService.duration,
      ignoreAppointmentId: appointmentId,
    });
  }, [appointmentId, businessId, chosenService, selectedDate, selectedStaff, state.appointments]);

  const canNext = () => {
    if (step === 0) return !!selectedService;
    if (step === 1) return !!selectedStaff;
    if (step === 2) return !!selectedTime;
    if (step === 3) return !!name.trim() && !!email.trim();
    return true;
  };

  const handleConfirm = async () => {
    setError("");
    if (!selectedService || !selectedStaff || !selectedTime) {
      setError("Please complete the booking details first.");
      return;
    }

    if (!name.trim()) {
      setError("Please enter your full name.");
      return;
    }

    if (!email.trim() || !email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }

    if (selectedDate < new Date().toISOString().slice(0, 10)) {
      setError("Please choose a current or future booking date.");
      return;
    }

    setIsSubmitting(true);
    const result = await bookAppointment({
      appointmentId,
      serviceId: selectedService,
      staffId: selectedStaff,
      date: selectedDate,
      startMinutes: timeStringToMinutes(selectedTime),
      name,
      email,
      phone,
    });

    if (!result.ok) {
      setError(result.message);
      setIsSubmitting(false);
      return;
    }

    setSuccessMessage(result.message);
    setStep(4);
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b bg-background/80 backdrop-blur-md">
        <div className="container flex h-14 items-center gap-4">
          <Link to="/customer/dashboard" className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <SchedoraLogo iconClassName="h-5 w-5" textClassName="text-base" />
          <span className="font-heading font-semibold">{appointmentId ? "Reschedule Appointment" : "Book Appointment"}</span>
        </div>
      </nav>

      <div className="container py-6">
        <div className="flex items-center justify-center gap-2 mb-8">
          {stepLabels.map((label, i) => (
            <div key={label} className="flex items-center gap-2">
              <div
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold transition-colors",
                  i < step
                    ? "bg-success text-success-foreground"
                    : i === step
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground",
                )}
              >
                {i < step ? <Check className="h-4 w-4" /> : i + 1}
              </div>
              <span className="hidden sm:inline text-xs font-medium text-muted-foreground">{label}</span>
              {i < stepLabels.length - 1 && <div className="hidden sm:block w-8 h-px bg-border" />}
            </div>
          ))}
        </div>

        <div className="mx-auto max-w-2xl">
          {step === 0 && (
            <div>
              <h2 className="text-xl font-heading font-bold mb-4">Choose a service</h2>
              <div className="grid gap-3">
                {services.map((s) => (
                  <ServiceCard
                    key={s.id}
                    {...s}
                    selectable
                    selected={selectedService === s.id}
                    onSelect={() => setSelectedService(s.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              <h2 className="text-xl font-heading font-bold mb-4">Choose your specialist</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                {staff.map((s) => (
                  <StaffCard
                    key={s.id}
                    {...s}
                    selectable
                    selected={selectedStaff === s.id}
                    onSelect={() => setSelectedStaff(s.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h2 className="text-xl font-heading font-bold mb-4">Pick a date & time</h2>
              <div className="mb-4">
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="mt-1 max-w-xs"
                />
              </div>
              <p className="text-sm text-muted-foreground mb-3">Available times</p>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {availableTimeSlots.map((t) => (
                  <button
                    key={t}
                    onClick={() => setSelectedTime(t)}
                    className={cn(
                      "rounded-lg border px-3 py-2 text-sm font-medium transition-colors hover:border-primary/30",
                      selectedTime === t && "bg-primary text-primary-foreground border-primary",
                    )}
                  >
                    {t}
                  </button>
                ))}
              </div>
              {selectedStaff && chosenService && availableTimeSlots.length === 0 && (
                <p className="mt-3 text-sm text-destructive">No available times for that specialist on this date.</p>
              )}
            </div>
          )}

          {step === 3 && (
            <div>
              <h2 className="text-xl font-heading font-bold mb-4">Your details</h2>
              <div className="space-y-4">
                <div>
                  <Label>Full Name</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" className="mt-1" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="jane@email.com"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Phone (optional)</Label>
                  <Input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="(555) 123-4567"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-success/10 mb-4">
                <Check className="h-8 w-8 text-success" />
              </div>
              <h2 className="text-2xl font-heading font-bold text-foreground">Booking Confirmed!</h2>
              <p className="mt-2 text-muted-foreground">{successMessage || (appointmentId ? "Your appointment has been updated." : "Your appointment has been booked.")}</p>
              <div className="mt-6 rounded-xl border bg-card p-6 text-left shadow-soft max-w-sm mx-auto space-y-2">
                <p className="text-sm">
                  <span className="text-muted-foreground">Service:</span> <span className="font-medium">{chosenService?.name}</span>
                </p>
                <p className="text-sm">
                  <span className="text-muted-foreground">With:</span> <span className="font-medium">{chosenStaff?.name}</span>
                </p>
                <p className="text-sm">
                  <span className="text-muted-foreground">Date:</span> <span className="font-medium">{selectedDate}</span>
                </p>
                <p className="text-sm">
                  <span className="text-muted-foreground">Time:</span> <span className="font-medium">{selectedTime}</span>
                </p>
                <p className="text-sm">
                  <span className="text-muted-foreground">Price:</span> <span className="font-medium">${chosenService?.price}</span>
                </p>
              </div>
              <Button className="mt-6" onClick={() => navigate("/customer/dashboard")}>Go to Dashboard</Button>
            </div>
          )}

          {step < 4 && (
            <div className="mt-8 flex justify-between">
              <Button variant="outline" disabled={step === 0} onClick={() => setStep((s) => s - 1)}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <div className="text-right">
                {error && <p className="mb-2 text-sm text-destructive">{error}</p>}
                <Button
                  disabled={!canNext() || isSubmitting}
                  onClick={() => {
                    if (step === 3) {
                      void handleConfirm();
                    } else {
                      setStep((s) => s + 1);
                    }
                  }}
                >
                  {step === 3
                    ? (isSubmitting ? "Saving..." : appointmentId ? "Save Changes" : "Confirm Booking")
                    : "Continue"} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
